import { useState, useMemo, useEffect } from 'react';
import { gql, useLazyQuery, useQuery, useMutation } from '@apollo/client';
import { Box, Button, AppBar, Tabs, Tab, Checkbox, CircularProgress, TextField, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { convertAllDates } from '../../utils/Functions';
import { selectUser } from '../../features/auth/authSlice';
import LoadingWrapper from '../common/LoadingWrapper';
import { IProject } from '../../types/project';
import { IUser } from '../../types/user';
import { checklist } from '../../utils/Constants';
import { useAppDispatch } from '../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import Dropdown from '../common/formControls/Dropdown';

interface IListColumn {
  header: string;
  width?: number | string;
  render: (row: any, index: number) => React.ReactNode;
}

const ListCard = ({ columns, rows, loading, emptyText }: { columns: IListColumn[]; rows: any[]; loading?: boolean; emptyText?: string }) => {
  const minWidth = columns.reduce((acc, col) => acc + (typeof col.width === 'number' ? col.width : 220), 0);

  return loading ? (
    <Box sx={{ textAlign: 'center', pt: 4 }}>
      <CircularProgress />
    </Box>
  ) : (
    <Box sx={{ border: '1px solid #e9e9e6', borderRadius: '10px', overflow: 'hidden' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: `${minWidth}px` }}>
          <Box sx={{ display: 'flex', backgroundColor: '#f1efe9', px: 2, py: 1.25 }}>
            {columns.map((col, index) => (
              <Box
                key={index}
                sx={{
                  flex: col.width ? 'none' : 1,
                  width: col.width,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: '#1a1a1a',
                  '@media (max-width:1499.98px)': { fontSize: '0.68rem' },
                }}
              >
                {col.header}
              </Box>
            ))}
          </Box>
          {rows.length ? (
            rows.map((row: any, index: number) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  borderTop: index === 0 ? 'none' : '1px solid #eeeeec',
                }}
              >
                {columns.map((col, colIndex) => (
                  <Box
                    key={colIndex}
                    sx={{
                      flex: col.width ? 'none' : 1,
                      width: col.width,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      pr: 1,
                      fontSize: '0.9rem',
                      color: '#333',
                      '@media (max-width:1499.98px)': { fontSize: '0.72rem' },
                    }}
                  >
                    {col.render(row, index)}
                  </Box>
                ))}
              </Box>
            ))
          ) : (
            <Box sx={{ px: 2, py: 3, textAlign: 'center', color: '#8a8a8a' }}>{emptyText ? emptyText : 'No Results Found'}</Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const SearchBar = ({ count, onSearch }: { count: number; onSearch: (value: string) => void }) => {
  const [searchValue, setSearchValue] = useState<string>('');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
      <TextField
        size="small"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSearch(searchValue);
          }
        }}
        placeholder={`${count} records...`}
        inputProps={{ 'aria-label': 'search' }}
        sx={{
          width: { xs: '100%', sm: '260px' },
          '@media (min-width:600px) and (max-width:1499.98px)': {
            width: '208px',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
            backgroundColor: '#f5f5f4',
            '& fieldset': { borderColor: '#e2e2e2' },
          },
        }}
      />
      <SearchIcon sx={{ cursor: 'pointer', color: '#8a8a8a' }} onClick={() => onSearch(searchValue)} />
      <FilterAltOffIcon
        sx={{ cursor: 'pointer', color: '#8a8a8a' }}
        onClick={() => {
          setSearchValue('');
          onSearch('');
        }}
      />
    </Box>
  );
};

const ExecutionTable = () => {
  const storeDispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [pageNumber, setPageNumber] = useState<number>(1);
  const [count, setCount] = useState<number>(0);
  const [documents, setDocuments] = useState([]);
  const [checklists, setChecklists] = useState<IChecklist[]>([]);
  const [selectedChecklist, setSelectedChecklist] = useState<IChecklist | null>(null);
  const [redirectLoading, setRedirectLoading] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState<string>(user.type === 'Developer' ? 'Verified' : 'Checklist');
  const { projectid } = useParams();

  const { loading: checklistLoading } = useQuery(GETCHECKLIST, {
    skip: user.type === 'Developer' || !projectid,
    variables: { filter: { project: projectid } },
    onCompleted: (data) => {
      let selected = data.checklistMany.find((checklist: IChecklist) => {
        return (
          new Date(checklist?.createdAt!).getMonth() === new Date().getMonth() &&
          new Date(checklist?.createdAt!).getDate() === new Date().getDate()
        );
      });
      let newChecklist = [
        ...data.checklistMany,
        {
          checklist: checklist,
          createdAt: new Date(),
        },
      ];
      if (selected) {
        newChecklist = data.checklistMany;
      }
      setChecklists(newChecklist);
      setSelectedChecklist(
        selected
          ? selected
          : {
              checklist: checklist,
              createdAt: new Date(),
            }
      );
    },
  });

  const [createChecklist] = useMutation(CREATECHECKLIST, {
    onCompleted: (data) => {
      setSelectedChecklist(data.checklistCreateOne.record);
      storeDispatch(showSuccessSnackbar('Completed Step!'));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar('Error completing step'));
      console.log(err, 'err');
    },
  });

  const [updateChecklist] = useMutation(UPDATECHECKLIST, {
    onCompleted: (data) => {
      setSelectedChecklist(data.checklistUpdateById.record);

      storeDispatch(showSuccessSnackbar('Completed Step!'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getDocuments, { loading }] = useLazyQuery(GETDOCUMENTS, {
    onCompleted: (data) => {
      let documentId = searchParams.get('document');
      let documents = data.getDocuments.documents
        .filter((document: any) => document._id !== documentId)
        .sort((a: any, b: any) => {
          if (a.executor === user.email) {
            return -1;
          }
        });
      // Remove Dov's Documents
      documents = documents.filter(
        (document: any) => document.deal.unit._id !== '62ffa0bfec7c0557a0251c7f' && document.deal.unit._id !== '62ffa0b9ec7c0557a0251bf5'
      );
      setDocuments(documents);
      setCount(data.getDocuments.count);
    },
  });

  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setPageNumber(1);
    setValue(newValue);
  };

  const [getDocumentUrl] = useLazyQuery(GETDOCUMENTURL, {
    onCompleted: (data) => {
      if (data) {
        window.location.href = data.getDocumentUrl;
      }
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  useEffect(() => {
    if (projectid && value !== 'Checklist') {
      getDocuments({
        variables: { project: projectid, type: value, page: pageNumber, perPage: 15, search: '' },
      });
    }
  }, [pageNumber, projectid, value]);

  const handleGlobalFilterValue = (textValue: string) => {
    setPageNumber(1);
    getDocuments({
      variables: { project: projectid, type: value, page: pageNumber, perPage: 15, search: textValue },
    });
  };

  const checklistColumns: IListColumn[] = useMemo(() => {
    return [
      {
        header: '',
        width: 40,
        render: (item: IItem, index: number) => (
          <Checkbox
            disabled={
              new Date(selectedChecklist?.createdAt!).getMonth() === new Date().getMonth() &&
              new Date(selectedChecklist?.createdAt!).getDate() === new Date().getDate()
                ? false
                : true
            }
            onChange={() => handleCompleteStep(item, index)}
            checked={item.completed ? true : false}
          />
        ),
      },
      {
        header: 'Name',
        render: (item: IItem) => (
          <Box
            sx={{
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#1a1a1a',
              '@media (max-width:1499.98px)': { fontSize: '0.76rem' },
            }}
          >
            {item.name}
          </Box>
        ),
      },
      {
        header: 'Date',
        width: 90,
        render: (item: IItem) => (
          <Box sx={{ fontSize: '0.85rem', color: '#8a8a8a', '@media (max-width:1499.98px)': { fontSize: '0.68rem' } }}>
            {item.completed ? convertAllDates(item.completed, 'PP') : ''}
          </Box>
        ),
      },
      {
        header: 'User',
        width: 90,
        render: (item: IItem) => (
          <Box sx={{ fontSize: '0.85rem', color: '#8a8a8a', '@media (max-width:1499.98px)': { fontSize: '0.68rem' } }}>
            {item.user ? item.user.fullName : ''}
          </Box>
        ),
      },
    ];
  }, [selectedChecklist]);

  const signedColumns: IListColumn[] = useMemo(() => {
    return [
      {
        header: 'Suite',
        width: 90,
        render: (rowData: any) => (
          <Link
            rel="noopener noreferrer"
            target="_blank"
            style={{ textDecoration: 'none', color: 'inherit' }}
            to={`/${projectid}/dashboard/${rowData.deal.unit._id}`}
          >
            <strong style={{ cursor: 'pointer' }}>{rowData.deal.unit.suite}</strong>
          </Link>
        ),
      },
      {
        header: 'Name',
        render: (rowData: any) => rowData.name,
      },
      {
        header: 'Updated At',
        width: 170,
        render: (rowData: any) => convertAllDates(rowData.updatedAt, 'PPpp'),
      },
    ];
  }, []);

  const columns: IListColumn[] = useMemo(() => {
    return [
      {
        header: 'Suite',
        width: 90,
        render: (rowData: any) => (
          <Link
            rel="noopener noreferrer"
            target="_blank"
            style={{ textDecoration: 'none', color: 'inherit' }}
            to={`/${projectid}/dashboard/${rowData.deal.unit._id}`}
          >
            <strong style={{ cursor: 'pointer' }}>{rowData.deal.unit.suite}</strong>
          </Link>
        ),
      },
      {
        header: 'Name',
        render: (rowData: any) => rowData.name,
      },
      {
        header: 'Executor',
        width: 160,
        render: (rowData: any) => rowData.executor,
      },
      {
        header: 'Updated At',
        width: 170,
        render: (rowData: any) => convertAllDates(rowData.updatedAt, 'PPpp'),
      },
      {
        header: 'Execute',
        width: 110,
        render: (rowData: any) => {
          if (rowData.executor === user?.email) {
            return (
              <Button size="small" onClick={() => handleOnCellClick(rowData)} variant="contained" color="primary">
                Execute
              </Button>
            );
          } else return null;
        },
      },
    ];
  }, []);

  const handleChecklistType = (event: any) => {
    if (event.target.value !== null) {
      let selected = checklists.find((checklist: IChecklist) => {
        return (
          new Date(checklist?.createdAt!).getMonth() === new Date(event.target.value).getMonth() &&
          new Date(checklist?.createdAt!).getDate() === new Date(event.target.value).getDate()
        );
      });
      setSelectedChecklist(selected!);
    }
  };

  const handleCompleteStep = async (data: IItem, index: number) => {
    if (!selectedChecklist?._id) {
      let newChecklistItems = checklist.map((checklist: IItem) => {
        if (data.name === checklist.name) {
          return {
            name: checklist.name,
            completed: new Date(),
            user: user._id,
          };
        } else return checklist;
      });

      createChecklist({
        variables: {
          record: {
            project: projectid,
            checklist: newChecklistItems,
          },
        },
      });
    } else {
      let newChecklistItems = selectedChecklist.checklist.map((checklist: IItem) => {
        if (data.name === checklist.name) {
          return {
            name: checklist.name,
            completed: data.completed ? null : new Date(),
            user: data.user ? null : user._id,
          };
        } else
          return {
            ...checklist,
            user: checklist.user ? checklist.user._id : null,
          };
      });

      updateChecklist({
        variables: {
          _id: selectedChecklist._id,
          record: {
            checklist: newChecklistItems,
          },
        },
      });
    }
  };

  const handleOnCellClick = (params: any) => {
    if (params.executor === user?.email) {
      setRedirectLoading(true);
      getDocumentUrl({
        variables: {
          project: projectid,
          executor: params.executor,
          unit: params.deal.unit._id,
          deal: params.deal._id,
          envelope: params.dsEnvelopeId,
        },
      });
    }
  };

  return redirectLoading ? (
    <LoadingWrapper title="Redirecting to Docusign..." modal={false} />
  ) : (
    <>
      <Box
        sx={{
          textAlign: 'center',
        }}
      >
        <AppBar position="static" color="transparent" elevation={0}>
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant={isMobile ? 'scrollable' : 'fullWidth'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
            aria-label="full width tabs example"
          >
            {user.type !== 'Developer' ? (
              <Tab
                sx={{
                  fontSize: {
                    sm: '12px',
                    xs: '10px',
                  },
                  '@media (min-width:600px) and (max-width:1499.98px)': {
                    fontSize: '9.6px',
                  },
                }}
                label="Checklist"
                value="Checklist"
              />
            ) : null}
            <Tab
              sx={{
                fontSize: {
                  sm: '12px',
                  xs: '10px',
                },
                '@media (min-width:600px) and (max-width:1499.98px)': {
                  fontSize: '9.6px',
                },
              }}
              label="To be executed"
              value="Verified"
            />
            {user.type !== 'Developer' ? (
              <Tab
                sx={{
                  fontSize: {
                    sm: '12px',
                    xs: '10px',
                  },
                  '@media (min-width:600px) and (max-width:1499.98px)': {
                    fontSize: '9.6px',
                  },
                }}
                label="Signed"
                value="Signed"
              />
            ) : null}
            {user.type !== 'Developer' ? (
              <Tab
                sx={{
                  fontSize: {
                    sm: '12px',
                    xs: '10px',
                  },
                  '@media (min-width:600px) and (max-width:1499.98px)': {
                    fontSize: '9.6px',
                  },
                }}
                label="Sent"
                value="Sent"
              />
            ) : null}
          </Tabs>
        </AppBar>
      </Box>
      <Box
        sx={{
          height: '360px',
          padding: '8px 16px',
          '@media (min-width:600px)': { height: '500px' },
          '@media (min-width:600px) and (max-width:1499.98px)': { height: '400px' },
        }}
      >
        {value === 'Checklist' ? (
          <Box
            sx={{
              height: '320px',
              overflowY: 'auto',
              '@media (min-width:600px)': { height: '450px' },
              '@media (min-width:600px) and (max-width:1499.98px)': { height: '360px' },
            }}
          >
            {checklists.length ? (
              <Box sx={{ width: '200px', mb: 2 }}>
                <Dropdown
                  id={'dropdown'}
                  size={'small'}
                  menuList={checklists.map((checklist: IChecklist) => convertAllDates(checklist.createdAt, 'PP'))}
                  name={'date'}
                  handleSelect={(e: any) => handleChecklistType(e)}
                  value={selectedChecklist ? convertAllDates(selectedChecklist?.createdAt, 'PP') : convertAllDates(new Date(), 'PP')}
                />
              </Box>
            ) : null}
            <ListCard
              loading={checklistLoading}
              columns={checklistColumns}
              rows={selectedChecklist ? selectedChecklist.checklist : checklist}
            />
          </Box>
        ) : null}
        {value === 'Verified' ? (
          <Box
            sx={{
              height: '360px',
              overflowY: 'auto',
              '@media (min-width:600px)': { height: '500px' },
              '@media (min-width:600px) and (max-width:1499.98px)': { height: '400px' },
            }}
          >
            <SearchBar count={count} onSearch={handleGlobalFilterValue} />
            <ListCard columns={columns} rows={documents} loading={loading} />
            <Box sx={{ textAlign: 'center' }}>
              <Button disabled={pageNumber === 1} onClick={() => setPageNumber(pageNumber - 1)}>
                {'<'}
              </Button>
              <span>{pageNumber}</span>
              <Button disabled={pageNumber === Math.ceil(count / 15)} onClick={() => setPageNumber(pageNumber + 1)}>
                {'>'}
              </Button>
            </Box>
          </Box>
        ) : null}
        {value === 'Sent' || value === 'Signed' ? (
          <Box
            sx={{
              height: '360px',
              overflowY: 'auto',
              '@media (min-width:600px)': { height: '500px' },
              '@media (min-width:600px) and (max-width:1499.98px)': { height: '400px' },
            }}
          >
            <SearchBar count={count} onSearch={handleGlobalFilterValue} />
            <ListCard columns={signedColumns} rows={documents} loading={loading} />
            <Box sx={{ textAlign: 'center' }}>
              <Button disabled={pageNumber === 1} onClick={() => setPageNumber(pageNumber - 1)}>
                {'<'}
              </Button>
              <span>{pageNumber}</span>
              <Button disabled={pageNumber === Math.ceil(count / 15)} onClick={() => setPageNumber(pageNumber + 1)}>
                {'>'}
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    </>
  );
};

export interface IChecklist {
  _id: string;
  project: IProject;
  checklist: IItem[];
  createdAt: Date;
  updatedAt: Date;
}

export interface IItem {
  name: string;
  completed: Date | null;
  user: IUser | null;
}

const GETCHECKLIST = gql`
  query checklistMany($filter: FilterFindManyChecklistInput) {
    checklistMany(filter: $filter) {
      _id
      project {
        _id
      }
      checklist {
        completed
        name
        user {
          _id
          fullName
        }
      }
      createdAt
    }
  }
`;

const CREATECHECKLIST = gql`
  mutation checklistCreateOne($record: CreateOneChecklistInput!) {
    checklistCreateOne(record: $record) {
      record {
        _id
        project {
          _id
        }
        checklist {
          completed
          name
          user {
            _id
            fullName
          }
        }
        createdAt
      }
    }
  }
`;

const UPDATECHECKLIST = gql`
  mutation checklistUpdateById($_id: MongoID!, $record: UpdateByIdChecklistInput!) {
    checklistUpdateById(_id: $_id, record: $record) {
      record {
        _id
        project {
          _id
        }
        checklist {
          completed
          name
          user {
            _id
            fullName
          }
        }
        createdAt
      }
    }
  }
`;

const GETDOCUMENTURL = gql`
  query getDocumentUrl($project: MongoID!, $executor: String!, $unit: MongoID!, $deal: MongoID!, $envelope: String!) {
    getDocumentUrl(project: $project, executor: $executor, unit: $unit, deal: $deal, envelope: $envelope)
  }
`;

const GETDOCUMENTS = gql`
  query getDocuments($project: MongoID!, $type: String!, $page: Float!, $perPage: Float!, $search: String) {
    getDocuments(project: $project, type: $type, page: $page, perPage: $perPage, search: $search) {
      documents {
        _id
        name
        status
        executor
        dsEnvelopeId
        updatedAt
        deal {
          _id
          unit {
            _id
            suite
            status
          }
          createdAt
        }
        updatedAt
      }
      count
    }
  }
`;

export default ExecutionTable;
